import {
  Button,
  CheckboxInput,
  Icon,
  Input,
  colors,
  DropdownItem,
  Popover,
} from '@dev-spendesk/grapes';
import classnames from 'classnames';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { FilteredListBySearchWrapper } from 'src/core/modules/organisation-reporting/components/common/FilteredListBySearchWrapper';
import { type EntityBasicInformation } from 'src/core/modules/organisation-reporting/hooks/data/useOrganisationCompanyList';

import type { FilterTypeOption } from '../../filter-type';
import { TypeListFilter } from './components/type-list-filters/TypeListFilter';

export const CostCenterPageHeader = ({
  onAddCostCenter,
  onSearch,
  typeFilter,
  setTypeFilter,
  entityFilters,
  setEntityFilters,
  companies,
}: {
  onAddCostCenter: () => void;
  onSearch: (value: string) => void;
  typeFilter: FilterTypeOption;
  setTypeFilter: (value: FilterTypeOption) => void;
  entityFilters: string[];
  setEntityFilters: (values: string[]) => void;
  companies: EntityBasicInformation[];
}) => {
  const { t } = useTranslation('global');

  const typeOptions: { key: FilterTypeOption; label: string }[] = [
    {
      key: 'cross-entity',
      label: t('multiEntityHub.costCenters.table.type.cross-entity'),
    },
    {
      key: 'entity-specific',
      label: t('multiEntityHub.costCenters.table.type.entity-specific'),
    },
  ];

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-12">
        <Input
          type="search"
          placeholder={t('multiEntityHub.costCenters.searchPlaceholder')}
          className="w-[240px]"
          onChange={(e) => onSearch(e.target.value)}
          leftAddon={
            <Icon
              name="magnifying-glass"
              color={colors.contentDecorativeIcon}
              className="ml-8"
              size="m"
            />
          }
        />

        <TypeListFilter
          label={t('multiEntityHub.costCenters.table.header.type')}
          activeFilters={typeFilter ? [typeFilter] : []}
          setActiveFilters={(values) => {
            setTypeFilter(values[0] ?? '');
          }}
          isMonoValue
          filterOptions={typeOptions}
        />

        <Popover
          renderTrigger={({ isDropdown, ...triggerProps }) => (
            <Button
              isDropdown
              variant={
                entityFilters.length ? 'secondaryInfo' : 'secondaryNeutral'
              }
              className={classnames(
                'text-content-primary body-m',
                entityFilters.length && 'text-content-selected',
              )}
              iconName="adjustments-horizontal"
              text={t('misc.entity')}
              {...triggerProps}
            />
          )}
        >
          {() => (
            <FilteredListBySearchWrapper
              items={companies.sort((a, b) => a.name.localeCompare(b.name))}
              searchableFields={['name']}
              isSearchEnabled
            >
              {({ filteredItems, search, setSearch }) => (
                <div className="m-8 flex flex-col gap-12">
                  <Input
                    className="min-w-[344px]"
                    leftAddon={
                      <Icon
                        color={colors.contentDecorativeIcon}
                        name="magnifying-glass"
                        className="ml-16 mr-8"
                      />
                    }
                    placeholder={t(
                      'organisation.reporting.page.entities.searchPlaceholder',
                    )}
                    onChange={(event) => setSearch(event.target.value)}
                    value={search}
                  />
                  <div className="flex max-h-[60vh] flex-col content-stretch overflow-auto">
                    {filteredItems.map((entity) => (
                      <button
                        key={entity.id}
                        type="button"
                        className="border-none bg-background-primary-default outline-none"
                        onClick={() => {
                          if (entityFilters.includes(entity.id)) {
                            setEntityFilters(
                              entityFilters.filter((id) => id !== entity.id),
                            );
                          } else {
                            setEntityFilters([...entityFilters, entity.id]);
                          }
                        }}
                      >
                        <DropdownItem
                          label={entity.name}
                          className="rounded-8 p-8 text-left text-content-primary body-m"
                          prefix={
                            <CheckboxInput
                              isChecked={entityFilters.includes(entity.id)}
                              onChange={() => {}}
                            />
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </FilteredListBySearchWrapper>
          )}
        </Popover>
      </div>
      <div className="flex items-center gap-12">
        <Button
          variant="primaryBrand"
          text={t('costCenters.addCostCenterDropDown.text')}
          onClick={onAddCostCenter}
        />
      </div>
    </div>
  );
};
